import React, { useEffect, useState } from "react";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { Calendar, MapPin, Users, Euro, Clock, Mail, Phone, FileText, User, AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatBudget, formatTravelDates, formatCreatedDate } from "@/components/utils/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface TravelRequestDetailsProps {
    travelRequest: TravelRequest;
}

export const TravelRequestDetails: React.FC<TravelRequestDetailsProps> = ({
    travelRequest,
}) => {
    const { t } = useLanguage();
    
    const calculateExpiryDate = (createdAt: string) => {
        const created = new Date(createdAt);
        const expiry = new Date(created.getTime() + 48 * 60 * 60 * 1000); // 48 ore
        return expiry;
    };

    const calculateIsExpired = (status: string, createdAt: string) => {
        if (status !== 'pending') {
            return false;
        }
        const now = new Date();
        const expiry = calculateExpiryDate(createdAt);
        return now.getTime() > expiry.getTime();
    }

    return (
        <div className="py-6 px-6 space-y-6">
            {/* Contatti - Sezione a 3 colonne */}
            <div className="space-y-2 mb-5">
                <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">
                        {t('travelRequest.contacts')}
                    </h4>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                        <Users className="h-3 w-3 text-gray-500" />
                        <span className="text-gray-700">
                            {travelRequest.contact_first_name} {travelRequest.contact_last_name}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-gray-500" />
                        <span className="text-gray-700">{travelRequest.contact_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Phone className="h-3 w-3 text-gray-500" />
                        <span className="text-gray-700">{travelRequest.contact_phone}</span>
                    </div>
                </div>
            </div>

            {/* Sezione principale - Destinazioni, Date e Gruppo */}
            <div className="grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 gap-4 mb-5">
                {/* Destinazioni */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('itinerary.destinations')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {travelRequest.destinations.length > 0
                            ? travelRequest.destinations.join(", ")
                            : t('travelRequest.destinationTBD')
                        }
                    </p>
                    {travelRequest.departure_point && (
                        <p className="text-xs text-gray-600">
                            {t('travelRequest.departureFrom')}: {travelRequest.departure_point}
                        </p>
                    )}
                </div>

                {/* Date di viaggio */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.travelDates')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {formatTravelDates(travelRequest.travel_start_date, travelRequest.travel_end_date, {
                            fallbackText: t('travelRequest.datesToBeDefined'),
                            showYear: true
                        })}
                    </p>
                    {travelRequest.travel_dates_flexible && (
                        <p className="text-xs text-blue-600">
                            {t('travelRequest.flexibleDates')}
                        </p>
                    )}
                </div>

                {/* Dettagli gruppo */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.group')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {travelRequest.total_guests} {travelRequest.total_guests === 1 ? t('travelRequest.person') : t('travelRequest.people')}
                    </p>
                    {travelRequest.guests_data && travelRequest.guests_data.length > 0 && (
                        <div className="space-y-1">
                            <p className="text-xs text-gray-600">{t('travelRequest.guestsDetails')}:</p>
                            <div className="flex flex-wrap gap-1">
                                {travelRequest.guests_data.map((guest) => (
                                    <span
                                        key={guest.id}
                                        className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                                    >
                                        {guest.age} {t('travelRequest.years')}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sezione Budget, Trasporto e Designer */}
            <div className="grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 gap-4 mb-5">
                {/* Budget */}
                <div className="space-y-2 pb-4">
                    <div className="flex items-center gap-2">
                        <Euro className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.budget')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {formatBudget(travelRequest.budget_min, travelRequest.budget_max)}
                    </p>
                    {travelRequest.budget_flexible && (
                        <p className="text-xs text-blue-600">
                            {t('travelRequest.flexibleBudget')}
                        </p>
                    )}
                </div>

                {/* Trasporto */}
                <div className="space-y-2 pb-4">
                    <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.transportation')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {travelRequest.needs_transportation ? t('travelRequest.transportationRequired') : t('travelRequest.transportationNotRequired')}
                    </p>
                </div>

                {/* Designer: mostra sia assegnato che preferito risolti dagli id */}
                <div className="space-y-2 pb-4">
                    <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.designer')}</h4>

                    {/* Designer assegnato */}
                    {travelRequest.assigned_designer_id ? (
                        <p className="text-gray-700 text-sm">
                            {t('travelRequest.assigned')}: {travelRequest?.td_name} {travelRequest?.td_surname}
                        </p>
                    ) : (
                        <>
                            <p className="text-gray-700 text-sm">{t('travelRequest.notAssigned')}</p>
                            {travelRequest?.travel_designer_preference ? (
                                <p className="text-gray-700 text-sm">
                                    {t('travelRequest.preferred')}: {travelRequest?.pref_name} {travelRequest?.pref_surname}
                                </p>
                            ) : (
                                <p className="text-gray-700 text-sm">{t('travelRequest.noPreference')}</p>
                            )}
                        </>
                    )}

                </div>


                {/* Interessi - Solo se presenti */}
                {travelRequest.interests && travelRequest.interests.length > 0 && (
                    <div className="space-y-2 pb-4">
                        <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-600" />
                            <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.interests')}</h4>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {travelRequest.interests.map((interest, index) => (
                                <span
                                    key={index}
                                    className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs"
                                >
                                    {interest}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>


            {/* Note aggiuntive - Solo se presenti */}
            {travelRequest.additional_notes && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.additionalNotes')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{travelRequest.additional_notes}</p>
                </div>
            )}


            {/* Alert per la scadenza */}
            {travelRequest.isPending && (
                <Alert variant="info" className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="font-medium">
                            {t('travelRequest.expiresOn')} {formatCreatedDate(calculateExpiryDate(travelRequest.created_at).toISOString(), { format: 'long' })} {t('travelRequest.at')} {calculateExpiryDate(travelRequest.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-sm mt-1 opacity-80">
                            {t('travelRequest.expiryMessage')}
                        </div>
                    </AlertDescription>
                </Alert>
            )}
            {calculateIsExpired(travelRequest.status, travelRequest.created_at) && (
                <Alert variant="destructive" className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="font-medium">
                            {t('travelRequest.expiredOn')} {formatCreatedDate(calculateExpiryDate(travelRequest.created_at).toISOString(), { format: 'long' })} {t('travelRequest.at')} {calculateExpiryDate(travelRequest.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-sm mt-1 opacity-80">
                            {t('travelRequest.expiredMessage')}
                        </div>
                    </AlertDescription>
                </Alert>
            )}
        </div>
    );
};

